<template>
  <div class="bg-white container mx-auto px-4 sm:px-6 lg:px-8 mb-10">
    <portal to="mobile-header" v-slot="{didToggle, toggle}">
      <div class="flex-grow min-w-0 flex flex-col align-center items-center justify-center h-12">
        <div class="align-center text-lg font-bold px-1 font-display inline-flex sm:hidden text-center truncate">
            {{group.name}}
        </div>
      </div>
    </portal>


    <header class="hidden sm:block py-10">
      <div class="md:flex md:items-center md:justify-between max-w-7xl mx-auto">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
            {{group.name}}
          </h2>
            <div class="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap">
              <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <use href="/images/regular.svg#user-circle"></use>
                </svg>
                Belongs to <TeacherNameOrYou :teacher="group.teacher" v-slot="{display}">
                  {{display}}
                </TeacherNameOrYou>
              </div>
              <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6" v-if="students">
                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <use href="/images/regular.svg#user-graduate"></use>
                </svg>
                {{this.studentCount}} students
              </div>
              <div class="mt-2 flex items-center text-sm leading-5 text-gray-500 sm:mr-6">
                <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <use href="/images/regular.svg#sort-size-down"></use>
                </svg>
                Sorted by&nbsp;
                <PanelDropdown
                  :inline="true"
                  :items="sortItems"
                >
                  <template slot="panel-dropdown__button">
                    {{sortDisplay}}
                  </template>
                </PanelDropdown>
              </div>
            </div>
          </div>

        <div class="mt-4 flex md:mt-0 md:ml-4">
          <Toggle v-slot="{ toggle, didToggle, didToggleOff }">
            <span class="relative z-10 inline-flex shadow-sm rounded-md">
              <router-link :to="{ name: 'groupEdit', params: {canGoBack: true}}" tag="button" type="button" class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150">
                Edit
              </router-link>
              <span class="-ml-px relative block">
                  <button
                    @click="didToggle"
                    v-click-outside="didToggleOff"
                    type="button" class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150" aria-label="Expand">
                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  <Transition
                    enter-active-class="transition ease-out duration-100 transform"
                    enter-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75 transform"
                    leave-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                  >
                    <div v-show="toggle" class="origin-top-right absolute right-0 mt-2 -mr-1 w-56 rounded-md shadow-lg">
                      <div class="rounded-md bg-white ring-1 ring-black ring-opacity-5">
                        <div class="py-1">
                          <RouterLink :to="{name: 'groupDelete'}" replace class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900">
                            Delete Group
                          </RouterLink>
                        </div>
                      </div>
                    </div>
                  </Transition>
              </span>
            </span>
          </Toggle>
        </div>
      </div>
    </header>
    <div class="p-4 flex flex-col items-center justify-center my-24" v-if="noStudents">
      <div class="text-6xl mb-4">👧🏽👦🏼👧🏻👦🏿</div>
      <div class="font-bold text-lg text-center">This group has no students in it.</div>
      <div class="mt-1 text-sm text-center">
        <router-link class="underline text-blue-600" :to="{name: 'groupEdit', params: {canGoBack: true}}">Edit this group</router-link> to add them in!</div>
    </div>
    <div v-else>
      <StudentList
        :students="students"
        paginate
        :canPagePrevious="canPagePrevious"
        :pageNext="pageNext"
        :pagePrevious="pagePrevious"
        :canPageNext="canPageNext"
        :count="studentList.count"
        :page="page"
        :pageSize="studentList.pageSize"
      />
    </div>
    <RouterView :group="group"></RouterView>
  </div>
</template>

<script>
import { preloadMany }      from '@/preloader'
import { tz }               from '@/tz'

import _has                 from 'lodash-es/has'
import _find                from 'lodash-es/find'
import _max                 from 'lodash-es/max'
import _min                 from 'lodash-es/min'
import _get                 from 'lodash-es/get'
import _map                 from 'lodash-es/map'

import Container            from '@/components/Container.vue'
import DeletedBanner        from '@/components/DeletedBanner.vue'
import PageHeader           from '@/components/PageHeader.vue'
import Panel                from '@/components/Panel.vue'
import PanelDropdown        from '@/components/PanelDropdown.vue'
import StudentList          from '@/components/StudentList.vue'
import Toggle               from '@/components/Toggle'

import TeacherNameOrYou     from '@/snippets/TeacherNameOrYou.vue'


import GroupQuery           from '@/queries/Group.gql'
import GroupDeleteMutation  from '@/mutations/GroupDelete.gql'
import GroupCleanseMutation from '@/mutations/GroupCleanse.gql'

const preloadQueries = (to) => {
  var pageNumber

  if (to.query.page) {
    pageNumber = parseInt(to.query.page)
  }

  return [
    {
      query: GroupQuery,
      queryName: 'group',
      variables: {
        id: to.params.groupId,
        tz: tz(),
        page: pageNumber || 1,
        sortBy: to.query.sortBy || 'ALPHA',
        sortDirection: to.query.sortDirection || 'ASC',
      },
      fetchPolicy: 'network-only'
    }
  ]
}

const SORT_NARRATIVES = [
  { narrative: 'alphabetical', sortBy: 'ALPHA', sortDirection: 'ASC' },
  { narrative: 'reverse alphabetical', sortBy: 'ALPHA', sortDirection: 'DESC' },
  { narrative: 'most contacts', sortBy: 'CONTACTS', sortDirection: 'DESC' },
  { narrative: 'fewest contacts', sortBy: 'CONTACTS', sortDirection: 'ASC' },
  { narrative: 'most recent', sortBy: 'RECENT', sortDirection: 'DESC' },
  { narrative: 'least recent', sortBy: 'RECENT', sortDirection: 'ASC' },
]

export default {
  components: {
    Container,
    DeletedBanner,
    PageHeader,
    Panel,
    PanelDropdown,
    StudentList,
    TeacherNameOrYou,
    Toggle,
  },

  props: {
    groupId: { required: true },
    teamId: { required: true }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  data () {
    return {
      group: { students: [] }
    }
  },

  computed: {

    canPage() {
      let pageSize = _get(this, 'group.studentList.pageSize', 1)

      return this.studentCount > pageSize
    },

    canPageNext() {
      let pageSize = _get(this, 'group.studentList.pageSize', 1)
      let remaining = this.studentCount - this.page * pageSize

      return remaining > 0
    },

    canPagePrevious() {
      return (this.page > 1)
    },

    iconName() {
      return _get(this, 'group.icon.name')
    },

    noStudents() {
      return this.students.length == 0
    },

    page() {
      var pageNumber

      if (this.$route.query.page) {
        pageNumber = parseInt(this.$route.query.page)
      }
      return pageNumber || 1
    },

    pageNext() {
      let pageSize = _get(this, 'group.studentList.pageSize', 1)
      let maxPage = Math.ceil(this.studentCount / pageSize)

      return {
        name: 'group',
        query: {
          page: _min([this.page + 1, maxPage]),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        }
      }
    },

    pagePrevious() {
      return {
        name: 'group',
        query: {
          page: _max([this.page - 1, 1]),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        }
      }
    },

    sortBy() {
      return this.$route.query.sortBy || 'ALPHA'
    },

    sortDirection() {
      return this.$route.query.sortDirection || 'ASC'
    },

    sortDisplay() {
      let foundNarrative = _find(SORT_NARRATIVES, (sortNarrative) => {
        let sortByMatch = sortNarrative.sortBy == this.sortBy
        let sortDirectionMatch = sortNarrative.sortDirection == this.sortDirection

        return sortByMatch && sortDirectionMatch
      })
      return _get(foundNarrative, 'narrative', 'alpha')
    },

    sortItems() {
      return _map(SORT_NARRATIVES, (sortNarrative) => {
        return {
          text: _get(sortNarrative, 'narrative'),
          to: {
            name: 'group',
            params: {
              groupId: this.groupId
            },
            query: {
              sortBy: _get(sortNarrative, 'sortBy'),
              sortDirection: _get(sortNarrative, 'sortDirection')
            }
          }
        }
      })
    },

    studentCount() {
      return _get(this, 'group.studentList.count', 0)
    },

    students() {
      return _get(this, 'group.studentList.list', [])
    },

    studentList() {
      return _get(this, 'group.studentList')
    },

    groupDropdownItems() {
      if ( this.group.deletedAt ) {
        return [
          // { text: 'Send a Blast' },
          { text: 'Edit', to: { name: 'groupEdit', params: { groupId: this.group.id } } },
          { text: 'Un-trash it', click: () => { this.didCleanseGroup() } }
        ]
      } else {
        return [
          // { text: 'Send a Blast' },
          { text: 'Edit', to: { name: 'groupEdit', params: { groupId: this.group.id } } },
          { text: 'Put it in the trash', click: () => { this.didDeleteGroup() } }
        ]
      }
    },
  },

  methods: {
    didCleanseGroup() {
      this.$apollo.mutate({
        mutation: GroupCleanseMutation,
        variables: {
          id: this.group.id,
          tz: tz()
        }
      })
      .then( (result) => {
        console.log('Cardstock: Group - successful GroupCleanse')
      })
      .catch((error) => {
        console.error("Cardstock: Group unhandleable error GroupCleanse")
        console.error(error)
      })
    },

    didDeleteGroup() {
      this.$apollo.mutate({
        mutation: GroupDeleteMutation,
        variables: {
          id: this.group.id,
          tz: tz()
        }
      })
      .then( (result) => {
        console.log('Cardstock: Group - successful GroupDelete')
      })
      .catch((error) => {
        console.error("Cardstock: Group unhandleable error")
        console.error(error)
      })
    },
  },

  apollo: {
    group: {
      query: GroupQuery,
      variables() {
        return {
          id: this.groupId,
          page: this.page,
          tz: tz(),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        }
      },
      fetchPolicy: 'cache-first'
    }
  }

}
</script>
